import { SITE } from "./Seo";

/* ─── Organization (site-wide) ─── */
export const organizationLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  logo: `${SITE.url}/logo512.png`,
  description:
    "B2B wholesaler of authentic aesthetic medical supplies — Korean botulinum toxins, hyaluronic acid fillers, biostimulators, skin boosters and exosomes for licensed clinics worldwide.",
  sameAs: [
    "https://www.trustpilot.com/review/wholesalefillers.world",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "hello@wholesalefillers.world",
      areaServed: ["EU", "US", "LATAM", "APAC", "Middle East"],
      availableLanguage: ["English"],
    },
  ],
});

/* ─── Website + sitelinks search ─── */
export const websiteLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE.url,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.url}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

/* ─── Breadcrumb list ─── */
export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url ? `${SITE.url}${it.url}` : undefined,
  })),
});

/* ─── Product + offers + reviews ─── */
export const productLd = (product) => {
  if (!product) return null;
  const summary = product.review_summary || {};
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    sku: product.id,
    category: product.category,
    description:
      product.extra?.short_description ||
      `${product.name} — authentic ${product.category} supplied by ${SITE.name}.`,
    image: (product.images || []).slice(0, 6),
    brand: { "@type": "Brand", name: product.extra?.brand || product.brand || "Various" },
    offers: {
      "@type": "Offer",
      url: `${SITE.url}/product/${product.slug || product.id}`,
      priceCurrency: "USD",
      price: Number(product.price || 0).toFixed(2),
      availability: product.in_stock !== false
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: SITE.name },
    },
  };
  if (summary?.count > 0) {
    ld.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: summary.avg,
      reviewCount: summary.count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  const reviews = (product.reviews || []).slice(0, 5);
  if (reviews.length) {
    ld.review = reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.name },
      datePublished: r.date,
      reviewBody: r.body,
      name: r.title,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
    }));
  }
  return ld;
};

/* ─── ItemList for category pages ─── */
export const itemListLd = (categoryName, products, basePath = "/product/") => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: `${categoryName} — Wholesale catalog`,
  numberOfItems: products.length,
  itemListElement: products.slice(0, 30).map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE.url}${basePath}${p.slug || p.id}`,
    name: p.name,
  })),
});

/* ─── BlogPosting ─── */
export const blogPostingLd = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.excerpt,
  image: [post.image],
  author: { "@type": "Person", name: post.author },
  datePublished: post.date,
  dateModified: post.date,
  publisher: {
    "@type": "Organization",
    name: SITE.name,
    logo: { "@type": "ImageObject", url: `${SITE.url}/logo512.png` },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE.url}/blog/${post.id}`,
  },
  articleSection: post.category,
});

/* ─── FAQPage ─── */
export const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});
